import { db } from "../lib/firebase";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  where,
} from "../lib/firestoreClient";
import { errorDev } from "../utils/logger";

const USERS = "users";
// Firestore `in` queries accept at most 30 document ids. Staying well
// below the limit keeps every chunk legal on every SDK version and avoids
// a composite-index requirement (a documentId `in` filter needs none).
const BATCH_CHUNK_SIZE = 10;

function mapDoc(snapshot) {
  return { id: snapshot.id, ...snapshot.data() };
}

/**
 * User profile reads.
 *
 * Several UI modules need "the profile document for this uid" and previously
 * each built `getDoc(doc(db, "users", id))` inline. Centralising it here keeps
 * profile reads behind the data layer and gives the boundary check something
 * concrete to point at while the remaining UI callers are migrated.
 *
 * `getById` is a single document read. `getByIds` batches many ids into
 * Firestore `in` queries (chunked under the 30-id limit) so a large
 * enrichment set costs a handful of round trips instead of one per id.
 * `productRepository.getByIds` still reads per id.
 */
export const userRepository = {
  path: USERS,

  async getById(userId) {
    if (!userId) return null;
    const snapshot = await getDoc(doc(db, USERS, String(userId)));
    return snapshot.exists() ? mapDoc(snapshot) : null;
  },

  async getByIds(userIds = []) {
    const uniqueIds = Array.from(new Set((userIds || []).filter(Boolean).map(String)));
    if (uniqueIds.length === 0) return [];
    if (uniqueIds.length === 1) {
      const row = await this.getById(uniqueIds[0]);
      return row ? [row] : [];
    }

    // Batched read: a single `in` query replaces up to BATCH_CHUNK_SIZE
    // individual document reads, so a 100-id wishlist enrichment costs
    // ~10 round trips instead of 100 concurrent ones. Document reads
    // (billing) are unchanged; only the number of query operations and
    // concurrent connections drops.
    const rowsById = new Map();

    const readChunk = async (chunk) => {
      try {
        const snap = await getDocs(
          query(collection(db, USERS), where(documentId(), "in", chunk))
        );
        snap.docs.forEach((d) => rowsById.set(d.id, mapDoc(d)));
      } catch (error) {
        // One unreadable chunk must not drop every id in it. Fall back
        // to individual reads so a partial failure keeps the rows that
        // did resolve — matching the per-id error semantics this method
        // always had.
        errorDev("userRepository: batched read failed, falling back", error);
        await Promise.all(
          chunk.map(async (id) => {
            try {
              const row = await this.getById(id);
              if (row) rowsById.set(id, row);
            } catch (err) {
              errorDev("userRepository: individual read failed", err);
            }
          })
        );
      }
    };

    for (let i = 0; i < uniqueIds.length; i += BATCH_CHUNK_SIZE) {
      await readChunk(uniqueIds.slice(i, i + BATCH_CHUNK_SIZE));
    }

    // `in` returns document-id order; restore the caller's ordering.
    return uniqueIds.filter((id) => rowsById.has(id)).map((id) => rowsById.get(id));
  },

  /**
   * Look up a single profile by email address, matching the admin
   * support-chat flow that previously built the email query inline.
   * Tries the raw address first, then the lowercase form, because
   * stored emails are not guaranteed to be normalised.
   *
   * @param {string} email
   * @returns {Promise<object|null>} mapped row, or null.
   */
  async findByEmail(email) {
    const normalized = String(email || "").trim();
    if (!normalized) return null;

    const candidates =
      normalized === normalized.toLowerCase()
        ? [normalized]
        : [normalized, normalized.toLowerCase()];

    for (const candidate of candidates) {
      try {
        const snap = await getDocs(
          query(collection(db, USERS), where("email", "==", candidate), limit(1))
        );
        if (!snap.empty) {
          const row = snap.docs[0];
          return { id: row.id, ...row.data() };
        }
      } catch (error) {
        errorDev("userRepository: email lookup failed", error);
      }
    }
    return null;
  },

  /**
   * Live profile listener. The hook needs the document the moment it changes
   * (counters, privacy flags, bio), so this stays a realtime subscription
   * rather than a one-time read.
   *
   * @param {string} userId
   * @param {Function} callback receives the mapped row (or null when absent).
   * @param {Function} [onError] optional error handler.
   * @returns {Function} unsubscribe.
   */
  subscribeById(userId, callback, onError = null) {
    if (!userId) {
      callback(null);
      return () => {};
    }
    return onSnapshot(
      doc(db, USERS, String(userId)),
      (snap) => {
        callback(snap.exists() ? mapDoc(snap) : null);
      },
      (error) => {
        errorDev("userRepository: profile listener failed", error);
        if (onError) onError(error);
      }
    );
  },

  /**
   * Merge-write a profile document. Used by the offline queue and the edit
   * flow, which both need the same `users/{uid}` write shape.
   *
   * @param {string} userId
   * @param {object} updateData
   * @param {object} [options] forwarded to `setDoc` (e.g. `{ merge: true }`).
   * @returns {Promise<void>}
   */
  async update(userId, updateData, { merge = true } = {}) {
    if (!userId) return;
    await setDoc(doc(db, USERS, String(userId)), updateData, { merge });
  },
};

export default userRepository;
